import React from "react";
import { Car, Gauge, Shield, ChevronRight } from "lucide-react";
import CammyDock from "./CammyDock";

// Icon names come from the client registry (`modules[].icon`) so the config can
// pick one without importing anything.
const ICONS = { car: Car, gauge: Gauge, shield: Shield };

/**
 * Post-login module picker.
 *
 * Shown for clients whose registry entry defines `modules` — Hegic's old portal
 * lands on a choice of Break-in / Claims / PAYD, and we mimic that in-app rather
 * than redirecting out to the three separate deployments.
 *
 * Parkhi is mounted here too, same component as the dashboard's (CammyDock.jsx)
 * — this page sits outside Layout entirely (Login.js renders it directly,
 * before the protected routes), so without this it would be the one screen in
 * the login→dashboard path where he never appears at all. His one-time-per-
 * login greeting (see PARKHI_INTRO_TOUR_KEY) fires on whichever of his mounts
 * happens first after a login, so a Hegic user sees it here and not again on
 * the dashboard a moment later.
 *
 * `permissions` is the comma-separated code list from GET /get_user_permissions,
 * which is what the old portal uses to decide which options a user may see. The
 * code-to-module mapping is not established yet, so every configured module is
 * shown for now; once the mapping is known, filter on it here (each module entry
 * gains a `permission` field) and nothing else needs to change.
 */
export default function ModuleSelect({ modules, permissions, onSelect }) {
  const visible = modules.filter((m) => m.enabled !== false);

  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        <h1 className="text-xl text-white text-center mb-8 drop-shadow-lg">
          Select Module
        </h1>

        <div className="flex flex-wrap gap-6 justify-center items-stretch">
          {visible.map((mod) => {
            const Icon = ICONS[mod.icon] || Car;
            const disabled = mod.available === false;

            return (
              <button
                key={mod.key}
                type="button"
                onClick={() => !disabled && onSelect(mod)}
                disabled={disabled}
                aria-label={mod.label}
                title={disabled ? mod.unavailableReason || "Not available yet" : mod.label}
                className={`group relative w-60 rounded-2xl px-6 py-6 text-left shadow-2xl transition-all duration-300 ${
                  disabled
                    ? "bg-gradient-to-r from-gray-700 to-gray-800 opacity-50 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 hover:scale-105 hover:shadow-blue-500/50"
                }`}
              >
                <div className="flex items-center gap-4">
                  <Icon
                    className={`w-9 h-9 shrink-0 ${disabled ? "text-gray-400" : "text-white"}`}
                  />
                  <span
                    className={`text-xl font-semibold ${disabled ? "text-gray-400" : "text-white"}`}
                  >
                    {mod.label}
                  </span>
                  {!disabled && (
                    <ChevronRight className="w-5 h-5 text-white/70 ml-auto shrink-0" />
                  )}
                </div>

                {mod.description && (
                  <p
                    className={`mt-3 text-sm leading-snug ${
                      disabled ? "text-gray-500" : "text-white/80"
                    }`}
                  >
                    {mod.description}
                  </p>
                )}

                {!disabled && (
                  <div className="absolute inset-0 rounded-2xl bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
                )}
              </button>
            );
          })}
        </div>

        {permissions && (
          <p className="mt-8 text-center text-xs text-white/40 font-noto-sans">
            Permissions: {permissions}
          </p>
        )}
      </div>

      {/* Desktop only, same as the dashboard's own gating — his fixed 300x292
          box would cover a third of a phone screen. */}
      <div className="hidden lg:block">
        <CammyDock glow />
      </div>
    </div>
  );
}
